import { useRef, useState, useEffect } from "react";
import "../styles/CropperModal.scss";
import AvatarEditor from "react-avatar-editor";
import { GuiButton } from "../components/global-ui/gui-button";
import { FileUploadButton } from "../components/image-library-modal/file-upload";

const CropperModal = ({
  src,
  modalOpen,
  setModalOpen,
  setPreview,
  setFormData,
  profilePhotoURL,
}) => {
  const [slideValue, setSlideValue] = useState(10);
  const cropRef = useRef(null);
  const originalFileRef = useRef(null);
  const [validationError, setValidationError] = useState("");

  const MAX_SIZE_MB = 5;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  async function fileFromBlobUrl(blobUrl, filename = "image.png") {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type });
    return file;
  }

  const prepareOriginalFile = async () => {
    if (modalOpen && typeof src === "string" && src.startsWith("blob:")) {
      try {
        const file = await fileFromBlobUrl(src);
        const allowedTypes = ["image/png", "image/jpeg"];
        if (!allowedTypes.includes(file.type)) {
          setValidationError("Only PNG and JPEG file formats are allowed.");
          originalFileRef.current = null;
          return;
        }
        originalFileRef.current = file;
      } catch (err) {
        console.error("Failed to convert blob URL to File:", err);
      }
    }
  };

  useEffect(() => {
    prepareOriginalFile();
  }, [modalOpen, src]);

  const handlefileChange = (file) => {
    setValidationError("");
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      setValidationError("Only PNG and JPEG file formats are allowed.");
      return;
    }

    originalFileRef.current = file;
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = () => {
    setValidationError("");

    if (originalFileRef.current) {
      if (originalFileRef.current.size > MAX_SIZE_BYTES) {
        setValidationError(
          `Image upload failed. The file exceeds the ${MAX_SIZE_MB}MB size limit. Please upload a smaller image.`
        );
        return;
      }
      setFormData(originalFileRef.current);
    }
    setModalOpen(false);
  };

  return (
    modalOpen && (
      <div className="cropper-modal">
        <div className="cropper-modal-content">
          <div className="cropper-modal-header">
            <span>Edit Photo</span>
            <i
              className="fa fa-close cursor-pointer"
              onClick={() => {
                setModalOpen(false);
                setPreview(profilePhotoURL);
              }}
            ></i>
          </div>
          <div className="cropper-modal-body">
            <div className="cropper-avatar-editor">
              <AvatarEditor
                ref={cropRef}
                image={src}
                style={{ width: "100%", height: "100%" }}
                border={10}
                borderRadius={100}
                color={[0, 0, 0, 0.72]}
                scale={slideValue / 10}
                rotate={0}
              />
            </div>

            <div className="cropper-slider-container">
              <i className="fa fa-minus"></i>
              <input
                type="range"
                min="10"
                max="50"
                value={slideValue}
                onChange={(e) => setSlideValue(e.target.value)}
                className="custom-slider"
              />
              <i className="fa fa-plus"></i>
            </div>
          </div>

          {/* Validation error label above Save button */}
          {validationError && <div class="error-class">{validationError}</div>}

          <div className="cropper-button-container">
            <GuiButton
              className="delete-btn"
              onClick={() => {
                setValidationError("");
                setModalOpen(false);
                setPreview(profilePhotoURL);
              }}
            >
              Cancel
            </GuiButton>
            <FileUploadButton
              name="logoUrl"
              onChange={(file) => handlefileChange(file)}
              acceptedTypes=".jpg, .jpeg, .png"
              maxFileSizeMB={MAX_SIZE_MB}
              isUploadBtn="true"
            />

            <GuiButton
              className="save-btn"
              disabled={!!validationError}
              onClick={handleSave}
            >
              Save
            </GuiButton>
          </div>
        </div>
      </div>
    )
  );
};

export default CropperModal;
